import { useState } from 'react'
import { router } from '@inertiajs/react'
import Text from '@arianna/design-system/Text'
import Button from '@arianna/design-system/Button'
import Card from '@arianna/design-system/Card'
import AdminLayout from '../../components/AdminLayout'

interface ManualRef {
  id: number
  title: string
  slug?: string
}

interface UserRow {
  id: number
  email: string
  created_at: string
  manuals: ManualRef[]
}

interface Props {
  users: UserRow[]
  all_manuals: ManualRef[]
}

function Avatar({ email }: { email: string }) {
  return (
    <span className="w-8 h-8 rounded-full bg-accent-bg text-accent flex items-center justify-center font-semibold text-sm shrink-0">
      {email.charAt(0).toUpperCase()}
    </span>
  )
}

function AccessPills({
  manuals,
  onRevoke,
}: {
  manuals: ManualRef[]
  onRevoke: (manualId: number) => void
}) {
  if (manuals.length === 0) {
    return (
      <Text variant="meta" className="text-ink-4">
        Nessuno
      </Text>
    )
  }

  return (
    <div className="flex flex-wrap gap-1">
      {manuals.map((m) => (
        <span
          key={m.id}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-surface-2 border border-line text-xs text-ink-2"
        >
          {m.title}
          <button
            type="button"
            aria-label={`Revoca accesso a ${m.title}`}
            onClick={() => onRevoke(m.id)}
            className="bg-transparent border-0 cursor-pointer p-0 leading-none text-ink-3 hover:text-red text-sm"
          >
            &times;
          </button>
        </span>
      ))}
    </div>
  )
}

function AssignControl({
  userEmail,
  assignable,
  totalManuals,
  value,
  onChange,
  onAssign,
}: {
  userEmail: string
  assignable: ManualRef[]
  totalManuals: number
  value: string
  onChange: (value: string) => void
  onAssign: () => void
}) {
  if (assignable.length === 0) {
    return (
      <Text variant="meta" className="text-ink-4">
        {totalManuals === 0 ? 'Nessun manuale' : 'Tutti assegnati'}
      </Text>
    )
  }

  return (
    <div className="flex gap-2 items-center">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm px-2 py-1.5 border border-line rounded-md bg-surface text-ink cursor-pointer"
        aria-label={`Seleziona manuale per ${userEmail}`}
      >
        <option value="">Seleziona...</option>
        {assignable.map((m) => (
          <option key={m.id} value={String(m.id)}>
            {m.title}
          </option>
        ))}
      </select>
      <Button size="sm" variant="accent" disabled={!value} onClick={onAssign}>
        Assegna
      </Button>
    </div>
  )
}

export default function AdminUsers({ users, all_manuals }: Props) {
  // Per-row selected manual id for the assign selector.
  // Key: user.id (number), value: string (the <select> value, or '')
  const [selections, setSelections] = useState<Record<number, string>>({})

  function selectedFor(userId: number): string {
    return selections[userId] ?? ''
  }

  function setSelectedFor(userId: number, value: string) {
    setSelections((prev) => ({ ...prev, [userId]: value }))
  }

  function handleRevoke(manualId: number, userId: number) {
    router.delete(`/admin/manuals/${manualId}/accesses/${userId}`, {
      preserveScroll: true,
    })
  }

  function handleAssign(userId: number) {
    const manualId = selectedFor(userId)
    if (!manualId) return
    router.post(
      `/admin/manuals/${manualId}/accesses`,
      { user_ids: [userId] },
      {
        preserveScroll: true,
        onSuccess: () => setSelectedFor(userId, ''),
      },
    )
  }

  return (
    <AdminLayout title="Utenti" active="users">
      {users.length === 0 && (
        <Text variant="body" className="text-ink-3">
          Nessun utente registrato ancora.
        </Text>
      )}

      {users.length > 0 && (
        <>
          {/* Desktop table */}
          <Card padding="sm" className="hidden md:block overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-line text-left">
                  <th className="px-4 py-3">
                    <Text variant="meta" className="font-semibold text-ink-2">
                      Utente
                    </Text>
                  </th>
                  <th className="px-4 py-3 whitespace-nowrap">
                    <Text variant="meta" className="font-semibold text-ink-2">
                      Registrato il
                    </Text>
                  </th>
                  <th className="px-4 py-3">
                    <Text variant="meta" className="font-semibold text-ink-2">
                      Accesso a
                    </Text>
                  </th>
                  <th className="px-4 py-3">
                    <Text variant="meta" className="font-semibold text-ink-2">
                      Assegna
                    </Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const assignedIds = new Set(user.manuals.map((m) => m.id))
                  const assignable = all_manuals.filter((m) => !assignedIds.has(m.id))

                  return (
                    <tr
                      key={user.id}
                      className="border-b border-line last:border-0 hover:bg-surface-2/40"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar email={user.email} />
                          <Text variant="ui" className="text-ink">
                            {user.email}
                          </Text>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Text variant="meta" className="text-ink-3">
                          {new Date(user.created_at).toLocaleDateString('it-IT')}
                        </Text>
                      </td>
                      <td className="px-4 py-3">
                        <AccessPills
                          manuals={user.manuals}
                          onRevoke={(manualId) => handleRevoke(manualId, user.id)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <AssignControl
                          userEmail={user.email}
                          assignable={assignable}
                          totalManuals={all_manuals.length}
                          value={selectedFor(user.id)}
                          onChange={(value) => setSelectedFor(user.id, value)}
                          onAssign={() => handleAssign(user.id)}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Card>

          {/* Mobile card list */}
          <div className="md:hidden flex flex-col gap-3">
            {users.map((user) => {
              const assignedIds = new Set(user.manuals.map((m) => m.id))
              const assignable = all_manuals.filter((m) => !assignedIds.has(m.id))

              return (
                <Card key={user.id} padding="md">
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar email={user.email} />
                    <div className="min-w-0">
                      <Text variant="ui" className="text-ink block truncate">
                        {user.email}
                      </Text>
                      <Text variant="meta" className="text-ink-3">
                        Registrato il {new Date(user.created_at).toLocaleDateString('it-IT')}
                      </Text>
                    </div>
                  </div>
                  <div className="mb-3">
                    <Text variant="meta" className="text-ink-3 block mb-1">
                      Accesso a
                    </Text>
                    <AccessPills
                      manuals={user.manuals}
                      onRevoke={(manualId) => handleRevoke(manualId, user.id)}
                    />
                  </div>
                  <div>
                    <Text variant="meta" className="text-ink-3 block mb-1">
                      Assegna manuale
                    </Text>
                    <AssignControl
                      userEmail={user.email}
                      assignable={assignable}
                      totalManuals={all_manuals.length}
                      value={selectedFor(user.id)}
                      onChange={(value) => setSelectedFor(user.id, value)}
                      onAssign={() => handleAssign(user.id)}
                    />
                  </div>
                </Card>
              )
            })}
          </div>
        </>
      )}
    </AdminLayout>
  )
}
